import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildExpiries } from './expiryModel'
import { chipDate, chipSub, SERIES_TITLE, seriesOf, withinHorizon } from './expirySeries'

/**
 * Every expiry on one row, as chips — ROADMAP 9.24.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT REPLACED A DROPDOWN, AND THAT IS THE WHOLE POINT
 *
 * `ExpiryPicker` was a calendar button with ‹ › either side: one expiry
 * visible, the rest behind a click. Choosing between near-dated contracts is
 * a COMPARISON — 5 days against 13 against 19 — and a control that shows one
 * option at a time cannot be compared against anything.
 *
 * So the whole series is on screen, each chip carrying its date and its
 * days-to-expiry, with the four that have a name wearing it (`expirySeries`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ARROWS SCROLL. THEY DO NOT SELECT.
 *
 * The picker's arrows stepped the SELECTION, so ‹ loaded a chain. Here they
 * move the strip by one chip and nothing else: a trader looking for the
 * January monthly should not place themselves in December on the way past,
 * and every intermediate chain would otherwise be fetched and rendered.
 *
 * Selecting is a click on the chip you meant, which is the gesture the whole
 * row exists to make possible.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THEY ARE ALWAYS DRAWN, AND DISABLED WHEN THEY WOULD DO NOTHING
 *
 * They used to be MOUNTED only on overflow, which is a feedback loop: adding
 * a 20px arrow narrows the scroll port by 20px, which can make a strip that
 * just fit overflow again, which mounts the other arrow, which narrows it
 * further. At the boundary the two states alternate and the chips twitch
 * sideways — the glitch this replaced.
 *
 * Reserving the space unconditionally breaks the loop: the port's width no
 * longer depends on the measurement taken from it. A disabled arrow also
 * says "this end is the end", which an absent one does not.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ExpiryStrip({
  expiries,
  value,
  onChange,
  onIntent,
}: {
  /** ISO dates, any order — the same shape `ExpiryPicker` took. */
  expiries: readonly string[]
  value: string | null
  onChange: (iso: string) => void
  /** Hover/focus, so the chain can be warmed before the click lands (4.8c). */
  onIntent?: (iso: string) => void
}) {
  const scroller = useRef<HTMLDivElement | null>(null)
  const [overflow, setOverflow] = useState({ left: false, right: false })
  /*
   * Built here rather than taken as items, so the call site passes exactly
   * what `ExpiryPicker` took and this stayed a drop-in replacement for it.
   */
  const items = useMemo(
    () => withinHorizon(buildExpiries(expiries), new Date(), value),
    [expiries, value],
  )
  /*
   * Tagged AFTER the horizon cut, which is the only order that is right:
   * CW/NW/CM/NM are positional over the series on screen, and tagging the
   * full list would be naming a current month the strip does not list.
   * Nothing the horizon removes is ever one of the four — they are the
   * four nearest — so in practice the two agree; the order makes that a
   * property of the code rather than a coincidence.
   */
  const series = useMemo(() => seriesOf(items), [items])

  /*
   * MEASURED, NOT INFERRED. `scrollWidth > clientWidth` is the only honest
   * test of whether a row overflows, and it answers for a long series in a
   * wide chain as well as a short one in a split.
   */
  const measure = useCallback(() => {
    const el = scroller.current
    if (!el) return
    const slack = el.scrollWidth - el.clientWidth
    const left = el.scrollLeft > 1
    // One pixel of tolerance: sub-pixel layout leaves a permanent sliver
    // of "overflow" that would strand an arrow enabled for ever.
    const right = slack > 1 && el.scrollLeft < slack - 1
    /*
     * ONLY WHEN IT ACTUALLY CHANGED. This runs on every `scroll` event, and
     * a smooth scroll fires one per frame: setting state unconditionally
     * re-rendered nineteen chips sixty times a second for the whole of a
     * single arrow press, which is the lag.
     */
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])

  useEffect(() => {
    const el = scroller.current
    if (!el) return
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure, items.length])

  /*
   * Keep the CHOSEN expiry in view when it changes from elsewhere — a chain
   * restored from storage, or a strategy that named its own expiry. Without
   * this the strip can open scrolled away from the contract on screen.
   *
   * ONLY WHEN IT IS GENUINELY OUT OF VIEW. `scrollIntoView` was running on
   * every selection, including a click on a chip already fully visible, and
   * `inline: 'nearest'` still nudges by a pixel or two — which, arriving
   * while an arrow's smooth scroll was mid-flight, is the double bounce.
   * Reading the chip's box against the port's answers the question outright.
   */
  useEffect(() => {
    const el = scroller.current
    if (!value || !el) return
    const chip = el.querySelector<HTMLElement>(`[data-iso="${value}"]`)
    if (!chip) return
    const left = chip.offsetLeft
    const right = left + chip.offsetWidth
    if (left >= el.scrollLeft && right <= el.scrollLeft + el.clientWidth) return
    // Centred, so a chip brought in from off-screen does not land flush
    // against the arrow that would scroll it away again.
    el.scrollTo({
      left: Math.max(0, left - (el.clientWidth - chip.offsetWidth) / 2),
      behavior: 'smooth',
    })
  }, [value])

  /**
   * One chip, in whichever direction.
   *
   * The step is measured from the first two chips rather than computed from
   * a width plus a literal gap: the gap is a stylesheet decision, and the
   * two drifted apart the moment it changed. Their offsets already contain it.
   */
  const nudge = (dir: -1 | 1) => {
    const el = scroller.current
    if (!el) return
    const chips = el.querySelectorAll<HTMLElement>('.tr-xchip')
    const first = chips[0]
    if (!first) return
    const second = chips[1]
    const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth
    el.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  if (items.length === 0) return <div className="tr-xstrip" />

  return (
    <div className="tr-xstrip">
      <button
        type="button"
        className="tr-xarrow"
        aria-label="Scroll expiries left"
        disabled={!overflow.left}
        onClick={() => nudge(-1)}
      >
        <ChevronLeft size={14} />
      </button>

      <div
        ref={scroller}
        className="tr-xscroll"
        role="tablist"
        aria-label="Expiry"
        onScroll={measure}
      >
        {items.map((item) => {
          const tag = series.get(item.iso)
          const active = item.iso === value
          return (
            <button
              key={item.iso}
              type="button"
              role="tab"
              aria-selected={active}
              data-iso={item.iso}
              data-testid={`expiry-chip-${item.iso}`}
              title={tag ? `${SERIES_TITLE[tag]} — ${item.dow} ${item.day}` : `${item.dow} ${item.day}`}
              className={cn('tr-xchip', active && 'on')}
              onClick={() => onChange(item.iso)}
              onMouseEnter={() => onIntent?.(item.iso)}
              onFocus={() => onIntent?.(item.iso)}
            >
              <span className="tr-xdate">{chipDate(item.iso)}</span>
              <span className={cn('tr-xsub', tag && 'tag')}>{chipSub(item.dte, tag)}</span>
            </button>
          )
        })}
      </div>

      <button
        type="button"
        className="tr-xarrow"
        aria-label="Scroll expiries right"
        disabled={!overflow.right}
        onClick={() => nudge(1)}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  )
}
